import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { useSeo } from '../lib/useSeo';
import { CalendarClock, ArrowLeft, Loader2, KeyRound, CheckCircle2 } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  useSeo({ title: 'Reset your password · Larkwell', description: 'Set a new password for your Larkwell account.', canonical: 'https://larkwellapp.com/reset-password' });

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Please choose at least 8 characters.'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords don\u2019t match.'); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset', { token, password: form.password });
      setDone(true);
      toast.success('Password updated \u2014 you can sign in now.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2 bg-[hsl(var(--background))]">
      <div className="relative hidden lg:flex flex-col justify-between p-10 overflow-hidden grain" style={{ background: 'hsl(var(--foreground))' }}>
        <div className="absolute inset-0 hero-wash opacity-70" />
        <div className="relative flex items-center gap-2 text-white">
          <div className="h-8 w-8 rounded-xl grid place-items-center" style={{ background: 'hsl(var(--primary))' }}><CalendarClock className="h-4 w-4" /></div>
          <span className="font-display text-lg font-700">Larkwell</span>
        </div>
        <div className="relative text-white">
          <h2 className="font-display text-3xl font-700 leading-tight max-w-sm">A fresh start, one tap away.</h2>
          <p className="text-white/70 mt-3 max-w-sm">Choose a new password and get back to a calmer, doable day.</p>
        </div>
        <div className="relative text-white/50 text-xs">Made with care for ADHD minds</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors mb-8"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>

          {!token ? (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-6 text-center" data-testid="reset-no-token">
              <h1 className="font-display text-xl font-700">Link looks incomplete</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">This reset link is missing its token. Please open the most recent reset email, or request a new link.</p>
              <Button className="w-full mt-5 h-11 rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={() => navigate('/login')} data-testid="reset-request-new">Request a new link</Button>
            </div>
          ) : done ? (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-6 text-center" data-testid="reset-done-card">
              <div className="h-12 w-12 rounded-2xl grid place-items-center mx-auto mb-3" style={{ background: 'hsl(var(--accent))' }}><CheckCircle2 className="h-6 w-6" style={{ color: 'hsl(var(--success))' }} /></div>
              <h1 className="font-display text-xl font-700">Password updated</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">Taking you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="h-12 w-12 rounded-2xl grid place-items-center mb-4" style={{ background: 'hsl(var(--accent))' }}><KeyRound className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} /></div>
              <h1 className="font-display text-2xl font-700">Choose a new password</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Make it something you’ll remember. At least 8 characters.</p>
              <form onSubmit={submit} className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="password">New password</Label>
                  <Input id="password" type="password" className="h-11 rounded-xl mt-1.5" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="reset-password-input" />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input id="confirm" type="password" className="h-11 rounded-xl mt-1.5" placeholder="••••••••" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl" style={{ background: 'hsl(var(--primary))' }} data-testid="reset-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
